import { useState } from 'react';
import { Button, Form, Tab, Tabs } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { FormikHelpers } from 'formik';
import type { CloudUploadScope } from 'api/apiCloudflare';
import { useAuth } from 'context/AuthContext';
import { hasUserAccess } from 'utils/helpers/access/hasUserAccess';
import spinnerTitle from 'components/others/spinnerTitle';
import SvgSave from 'components/svg/SvgSave';
import type { ProductEditType } from '../types';
import ProductFormAdditional from './tabs/ProductFormAdditional';
import ProductFormFiles from './tabs/ProductFormFiles';
import ProductFormIamges from './tabs/ProductFormImages';
import ProductFormGeneral from './tabs/ProductFormGeneral';
import SolarIcon from 'components/icons/SolarIcon';

interface ProductFormProps {
    product: ProductEditType;
    handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
    isSubmitting: boolean;
    setFieldValue: FormikHelpers<ProductEditType>['setFieldValue'];
    values: ProductEditType;
    ensureSaved: () => Promise<CloudUploadScope | false>;
}

const ProductForm = ({
    product,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    values,
    ensureSaved,
}: ProductFormProps) => {
    const { auth } = useAuth();
    const { t } = useTranslation(['Products', 'common']);
    const [activeTab, setActiveTab] = useState('base');

    return (
        <Form onSubmit={handleSubmit} noValidate className="d-flex flex-column h-100">
            <div className="off-product-card-tabs d-flex flex-column h-100">
                <div className="off-product-card-tabs-content px-3">
                    <Tabs
                        variant="underline"
                        activeKey={activeTab}
                        onSelect={(tab) => tab && setActiveTab(tab)}
                        className="mb-3 invoice-tab border-bottom"
                    >
                        <Tab
                            eventKey="base"
                            title={
                                <>
                                    <SolarIcon name="gear" className="me-2 fs-14" />
                                    {t('tabGeneral')}
                                </>
                            }
                        >
                            <ProductFormGeneral
                                product={product}
                                isSubmitting={isSubmitting}
                                setFieldValue={setFieldValue}
                                values={values}
                            />
                        </Tab>

                        <Tab
                            eventKey="files"
                            title={
                                <>
                                    <SolarIcon name="paperclip" className="me-2 fs-14" />
                                    {t('tabFiles')}
                                </>
                            }
                        >
                            <div className="pb-4">
                                <ProductFormIamges
                                    isSubmitting={isSubmitting}
                                    setFieldValue={setFieldValue}
                                    values={values}
                                />
                                <ProductFormFiles
                                    setFieldValue={setFieldValue}
                                    values={values}
                                    ensureSaved={ensureSaved}
                                />
                            </div>
                        </Tab>

                        <Tab
                            eventKey="additional"
                            title={
                                <>
                                    <SolarIcon name="card-list" className="me-2 fs-14" />
                                    {t('tabAdditional')}
                                </>
                            }
                        >
                            <ProductFormAdditional values={values} />
                        </Tab>
                    </Tabs>
                </div>

                {activeTab === 'base' && (
                    <div className="p-3 offcanvas-footer off-product-card-tabs-footer">
                        {hasUserAccess(auth?.user, 'prodEdit') ? (
                            <div className="d-flex justify-content-end">
                                <Button type="submit" variant="info" disabled={isSubmitting} className="min100px">
                                    {spinnerTitle(
                                        <>
                                            <SvgSave className="me-2 mtop-2" />
                                            {t('common:buttons.save')}
                                        </>,
                                        isSubmitting,
                                        t('common:buttons.saving'),
                                    )}
                                </Button>
                            </div>
                        ) : (
                            <div className="alert alert-warning text-start" role="alert">
                                {t('common:messReadOnly')}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Form>
    );
};

export default ProductForm;
